import React, { useCallback, useRef, useEffect, useState } from "react";
import { Box, DropdownBody, DropdownItem, Icons, useToast } from "fds";
import "./index.scss";
import {
  DocInfo,
  DocumentListReq,
  favoriteReq,
  FolderInfoDTO,
  MenuListType,
} from "../../types/files";
import utilsCommon from "../../shared/utils/common";
import ShareLinkModal from "../More/ShareLinkModal";
import ChangeFileNameModal from "../More/ChangeFileNameModal";
import ReadRequestModal from "../More/ReadRequestModal";
import { nanoid } from "@reduxjs/toolkit";
import ManaulRevisionCompleteModal from "../More/ManaulRevisionCompleteModal";
import WorkgroupAddModal from "../WorkgroupAddModal";
import LeaveCommentModal from "../More/LeaveCommentModal";
import FolderMoveModal from "../More/FolderMoveModal";
import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import ConfirmDeleteOrRestoreModal from "../More/ConfirmDeleteOrRestoreModal";
import ChangeFileAuthModal from "../More/ChangeFileAuthModal";
import CreateNewFolderModal from "../../pages/Workgroup/WorkgroupFile/Modal/CreateNewFolderModal";
import DeleteFolderModal from "../../pages/Workgroup/WorkgroupFile/Modal/DeleteFolderModal";
import {
  handleDocPreview,
  doUnauthDocDownload,
  doDeleteDocument,
  handleDocDownload,
  handleSetContentFavorite,
} from "../../shared/utils/api";
import useStartDownload from "../../shared/hooks/useStartDownload";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import { useTranslation } from "react-i18next";
import ChangeWorkgroupFileAuthModal from "../More/ChangeWorkgroupFileAuthModal";
interface DocSettingButtonProps {
  settingButtonInfo: {
    x: number;
    y: number;
    syncId: string;
    docInfo?: DocInfo;
    folderInfo?: FolderInfoDTO;
  };
  setDocSettingInfo: (req: {
    x: number;
    y: number;
    syncId: string;
    docInfo?: DocInfo;
    folderInfo?: FolderInfoDTO;
  }) => void;
  page?: string;
  filter?: DocumentListReq | favoriteReq;
  requiredRecallSyncId?: string[];
  changeRequiredRecallSyncIds?: (syncIds: string[]) => void;
  handleDeleteAfter: (syncIds: string[]) => void;
}
const DocSettingButton: React.FC<DocSettingButtonProps> = ({
  settingButtonInfo,
  page,
  filter,
  requiredRecallSyncId,
  changeRequiredRecallSyncIds,
  setDocSettingInfo,
  handleDeleteAfter,
}) => {
  const { t } = useTranslation();

  const inputRef = useRef<HTMLTableRowElement>(null);
  const toast = useToast();
  const {
    getContentDownload,
    getDocDecryptDownload,
    getContentDetail,
    getDocDelete,
    getContentCheckout,
    getContentList,
    getContentUndoCheckout,
    forceCheckinByWeb,
    getContentFavorite,
  } = useDocumentStore();
  const startDownload = useStartDownload();
  const { updateStarItemFavorite } = useWorkgroupStore();
  const direction = document.documentElement.dir; // 'ltr' 또는 'rtl'
  const [height, setHeight] = useState(0);
  const [modalType, changeModalType] = useState("CLOSE");
  let menus = [
    {
      key: "preview",
      resource: t("file.action.open"),
      icon: <Icons variant="line" label="preview" isOn />,
      onClick: () =>
        settingButtonInfo.docInfo &&
        handleDocPreview({
          permission: "view",
          workgroupId: settingButtonInfo.docInfo?.workgroup.workgroupId,
          syncId: settingButtonInfo.docInfo?.syncId,
          toast,
          t,
          revisionNo: settingButtonInfo.docInfo?.revisionNo,
        }),
    },
    {
      key: "shareLink",
      resource: t("file.action.shareLink"),
      icon: <Icons variant="line" label="link" />,
      onClick: () => changeModalType("SHARE_LINK"),
    },
    {
      key: "download",
      resource: t("file.action.download"),
      icon: <Icons variant="line" label="download" />,
      onClick: () =>
        handleDocDownload(
          settingButtonInfo.syncId,
          getContentDownload,
          toast,
          t
        ),
    },
    {
      key: "shareWorkgroup",
      resource: t("file.action.shareToOtherWorkgroup"),
      icon: <Icons variant="line" label="people" />,
      onClick: () => changeModalType("SHARE_FILE_TO_WORKGROUP"),
    },
    {
      key: "favorite",
      resource: t("file.action.removeFav"),
      icon: <Icons variant="line" label="star" isOn />,
      onClick: () =>
        handleSetContentFavorite({
          docInfos: settingButtonInfo.docInfo
            ? [settingButtonInfo.docInfo]
            : undefined,
          getContentFavorite: getContentFavorite,
          page: page,
          handleAfterSubmit: () => {
            setDocSettingInfo({
              x: 0,
              y: 0,
              syncId: "",
              docInfo: undefined,
            });
            updateStarItemFavorite({
              syncIds: [settingButtonInfo.docInfo?.syncId ?? ""],
              flag:
                !!settingButtonInfo.docInfo &&
                settingButtonInfo.docInfo?.favoritDate <= 0,
            });
          },
          flag:
            settingButtonInfo.docInfo &&
            settingButtonInfo.docInfo?.favoritDate <= 0,
        }),
    },
    {
      key: "notFavorite",
      resource: t("file.action.addFav"),
      icon: <Icons variant="fill" label="star" stroke="#F6C243" />,
      onClick: () =>
        handleSetContentFavorite({
          docInfos: settingButtonInfo.docInfo
            ? [settingButtonInfo.docInfo]
            : undefined,
          getContentFavorite: getContentFavorite,
          page: page,
          handleAfterSubmit: () => {
            setDocSettingInfo({
              x: 0,
              y: 0,
              syncId: "",
              docInfo: undefined,
            });
            updateStarItemFavorite({
              syncIds: [settingButtonInfo.docInfo?.syncId ?? ""],
              flag:
                !!settingButtonInfo.docInfo &&
                settingButtonInfo.docInfo?.favoritDate <= 0,
            });
          },
          flag:
            settingButtonInfo.docInfo &&
            settingButtonInfo.docInfo?.favoritDate <= 0,
        }),
    },
    {
      key: "checkout",
      resource: t("file.action.manualRevStart"),
      icon: <Icons variant="line" label="manualStart" />,
      onClick: () => handleDocCheckout(),
    },
    {
      key: "checkin",
      resource: t("file.action.manualRevComplete"),
      icon: <Icons variant="line" label="manualComplete" />,
      onClick: () => changeModalType("MANUAL_REVISION_COMPLETE"),
    },
    {
      key: "undoCheckout",
      resource: t("file.action.manualRevCancel"),
      icon: <Icons variant="line" label="manualCancel" />,
      onClick: () => handleDocCheckCancel(),
    },
    {
      key: "forceCheckin",
      resource: t("file.action.forceRevComplete"),
      icon: <Icons variant="line" label="editExit" />,
      onClick: () => handleDocForceComplete(),
    },
    {
      key: "auth",
      resource: t("file.action.auth"),
      icon: <Icons variant="line" label="access" />,
      onClick: () =>
        settingButtonInfo.docInfo?.workgroup.workgroupId === "NONEWORKGROUP"
          ? changeModalType("CHANGE_FILE_AUTH")
          : changeModalType("CHANGE_FILE_AUTH_WORKGROUP"),
    },
    {
      key: "requestOpen",
      resource: t("file.action.sendReadRequest"),
      icon: <Icons variant="line" label="alertOctagon" />,
      onClick: () => changeModalType("READ_REQUEST"),
    },
    {
      key: "leaveComment",
      resource: t("file.action.leaveComment"),
      icon: <Icons variant="line" label="memo" />,
      onClick: () => changeModalType("LEAVE_MEMO"),
    },
    {
      key: "unAuthDownload",
      resource: t("file.action.decryptedDownload"),
      icon: <Icons variant="line" label="unlock" />,
      onClick: () => {
        if (!settingButtonInfo.docInfo) return;
        doUnauthDocDownload(
          settingButtonInfo.docInfo,
          toast,
          getDocDecryptDownload,
          startDownload
        );
        handleTurnOffSettng();
      },
    },
    {
      key: "changeName",
      resource: t("file.action.rename"),
      icon: <Icons variant="line" label="edit2" />,
      onClick: () => changeModalType("CHANGE_FILE_NAME"),
    },
    {
      key: "restore",
      resource: t("trash.action.restore"),
      icon: <Icons variant="line" label="redoLeft" />,
      onClick: () => changeModalType("RESTORE_FILE"),
    },
    {
      key: "deletePerm",
      resource: t("trash.action.permDelete"),
      icon: <Icons variant="line" label="delete" />,
      onClick: () => changeModalType("DELETE_PERM"),
    },
    {
      key: "changeFolderName",
      resource: t("file.action.rename"),
      icon: <Icons variant="line" label="edit2" />,
      onClick: () => changeModalType("CHANGE_FOLDER_NAME"),
    },

    {
      key: "folderMove",
      resource: t("common.move"),
      icon: <Icons variant="line" label="foldermove" />,
      onClick: () => changeModalType("FOLDER_MOVE"),
    },
    // { key: "googleUpload" },
  ] as {
    key: MenuListType;
    resource: string;
    icon: React.ReactNode;
    onClick?: () => void;
  }[];

  useEffect(() => {
    if (
      requiredRecallSyncId?.includes(settingButtonInfo.syncId) &&
      changeRequiredRecallSyncIds
    ) {
      changeRequiredRecallSyncIds(
        requiredRecallSyncId?.filter(
          (item) => item !== settingButtonInfo.syncId
        ) ?? []
      );
      getContentDetail({
        workgroupId:
          settingButtonInfo.docInfo?.workgroup?.workgroupId ?? "NONEWORKGORUP",
        syncId: settingButtonInfo.syncId,
      }).then((resp: any) => {
        setDocSettingInfo({
          ...settingButtonInfo,
          docInfo: resp.payload.documentInfo,
        });
      });
    }
  }, [requiredRecallSyncId]);
  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  });
  useEffect(() => {
    if (!inputRef.current) return;
    const observer = new ResizeObserver(() => {
      if (inputRef.current) {
        setHeight(inputRef.current.offsetHeight);
      }
    });
    observer.observe(inputRef.current);
    return () => observer.disconnect();
  }, []);
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (inputRef.current && !(inputRef.current! as any).contains(e.target)) {
      handleTurnOffSettng();
      document.removeEventListener("click", handleClickOutside, true);
    }
  }, []);
  const handleTurnOffSettng = (): void => {
    setDocSettingInfo({
      x: 0,
      y: 0,
      syncId: "",
      docInfo: undefined,
    });
    changeModalType("CLOSE");
  };
  /** 수동 리비전 시작 */
  const handleDocCheckout = async () => {
    if (!settingButtonInfo.docInfo) return;
    let resp: any = await getContentCheckout({
      workgroupId: settingButtonInfo.docInfo?.workgroup.workgroupId,
      syncId: settingButtonInfo.docInfo?.syncId,
      revisionNo: settingButtonInfo.docInfo.revisionNo,
    });
    if (resp.payload) {
      await getContentDownload({
        syncId: settingButtonInfo.docInfo?.syncId,
        revisionNo: settingButtonInfo.docInfo.revisionNo + "",
        startDownload: startDownload,
      });
      toast.toastMsg(nanoid(), t("file.manualRev.alert.start"), "success");
      if (filter && typeof filter === "object" && "folderCode" in filter) {
        getContentList(filter as DocumentListReq);
      }
    } else {
      toast.toastMsg(nanoid(), resp.error.message, "error");
    }
    handleTurnOffSettng();
  };
  /** 수동 리비전 취소 */
  const handleDocCheckCancel = async () => {
    if (!settingButtonInfo.docInfo) return;
    let resp: any = await getContentUndoCheckout({
      workgroupId: settingButtonInfo.docInfo?.workgroup.workgroupId,
      syncId: settingButtonInfo.docInfo?.syncId,
      revisionNo: settingButtonInfo.docInfo.revisionNo,
    });
    if (resp.payload) {
      toast.toastMsg(
        nanoid(),
        t("file.manualRev.alert.cancelSuccess"),
        "success"
      );
      if (filter && typeof filter === "object" && "folderCode" in filter) {
        getContentList(filter as DocumentListReq);
      }
    } else {
      toast.toastMsg(
        nanoid(),
        resp.error.message ?? t("file.manualRev.alert.cancelFail"),
        "error"
      );
    }
    handleTurnOffSettng();
  };
  /** 수동 리비전 강제 완료 */
  const handleDocForceComplete = async () => {
    if (!settingButtonInfo.docInfo) return;
    let resp: any = await forceCheckinByWeb(settingButtonInfo.docInfo?.syncId);
    if (resp.payload) {
      toast.toastMsg(nanoid(), t("file.alert.forceDeleteSuccess"), "success");
      if (filter && typeof filter === "object" && "folderCode" in filter) {
        getContentList(filter as DocumentListReq);
      }
    } else {
      toast.toastMsg(
        nanoid(),
        resp.error.message ?? t("file.alert.forceDeleteFail"),
        "error"
      );
    }
    handleTurnOffSettng();
  };
  return (
    <>
      {modalType === "CLOSE" ? (
        <DropdownBody
          maxWidth={page === "trash" ? undefined : 300}
          dropdownRef={inputRef}
          minWidth={190}
          width={"fit-content"}
          maxHeight={window.innerHeight - 120}
          location={{
            x:
              settingButtonInfo.x -
              (direction === "ltr" &&
              settingButtonInfo.x > window.innerWidth / 2
                ? 190
                : 0) -
              (direction === "rtl" && window.eco.companyName === "linkApp"
                ? 38
                : 0),
            y:
              settingButtonInfo.y -
              (settingButtonInfo.y > window.innerHeight / 2 ? height : 0),
          }}
        >
          <Box
            direction={"column"}
            maxHeight={
              utilsCommon.checkMenuAvailble(
                "delete",
                settingButtonInfo.docInfo ?? settingButtonInfo.folderInfo,
                page
              )
                ? 296
                : 342
            }
            classes="overflow-scroll"
          >
            {menus
              .filter((item) =>
                utilsCommon.checkMenuAvailble(
                  item.key,
                  settingButtonInfo.docInfo ?? settingButtonInfo.folderInfo,
                  page
                )
              )
              .map((item) => (
                <DropdownItem
                  height={item.key === "divider" ? undefined : 20}
                  type={
                    item.key.includes("delete")
                      ? "warning"
                      : item.key === "checkin"
                      ? "green"
                      : "default"
                  }
                  state={item.key === "divider" ? "divider" : "default"}
                  label={item.resource}
                  frontIcon={item.icon}
                  onClick={item.onClick}
                />
              ))}
          </Box>
          {[
            {
              key: "divider",
            },
            {
              key: "folderdelete",
              resource: t("file.action.deleteFolder"),
              icon: <Icons variant="line" label="delete" />,
              onClick: () => changeModalType("FOLDER_DELETE"),
            },
            {
              key: "delete",
              resource: t("common.delete"),
              icon: <Icons variant="line" label="delete" />,
              onClick: () =>
                !!settingButtonInfo?.docInfo?.syncId &&
                doDeleteDocument(
                  [settingButtonInfo?.docInfo],
                  toast,
                  getDocDelete,
                  () => {
                    handleDeleteAfter &&
                      handleDeleteAfter(
                        settingButtonInfo?.docInfo?.syncId
                          ? [settingButtonInfo?.docInfo?.syncId]
                          : []
                      );
                    handleTurnOffSettng();
                  }
                ),
            },
          ]
            .filter((item) =>
              utilsCommon.checkMenuAvailble(
                item.key,
                settingButtonInfo.docInfo ?? settingButtonInfo.folderInfo,
                page
              )
            )
            .map((item) => (
              <DropdownItem
                height={item.key === "divider" ? undefined : 20}
                type={item.key.includes("delete") ? "warning" : "default"}
                state={item.key === "divider" ? "divider" : "default"}
                label={item.resource}
                frontIcon={item.icon}
                onClick={item.onClick}
              />
            ))}
        </DropdownBody>
      ) : (
        <>
          {modalType === "SHARE_LINK" && (
            <ShareLinkModal
              open={modalType === "SHARE_LINK"}
              onClose={handleTurnOffSettng}
              docInfo={settingButtonInfo.docInfo}
            />
          )}
          {modalType === "CHANGE_FILE_NAME" && (
            <ChangeFileNameModal
              open={modalType === "CHANGE_FILE_NAME"}
              onClose={handleTurnOffSettng}
              docInfo={settingButtonInfo.docInfo}
            />
          )}
          {modalType === "READ_REQUEST" && (
            <ReadRequestModal
              open={modalType === "READ_REQUEST"}
              onClose={handleTurnOffSettng}
              docInfo={settingButtonInfo.docInfo}
            />
          )}
          {modalType === "MANUAL_REVISION_COMPLETE" && (
            <ManaulRevisionCompleteModal
              open={modalType === "MANUAL_REVISION_COMPLETE"}
              onClose={handleTurnOffSettng}
              docInfo={settingButtonInfo.docInfo}
            />
          )}
          {modalType === "LEAVE_MEMO" && (
            <LeaveCommentModal
              open={true}
              onClose={handleTurnOffSettng}
              docInfo={settingButtonInfo.docInfo}
            />
          )}
          {modalType === "FOLDER_MOVE" && settingButtonInfo.docInfo && (
            <FolderMoveModal
              open={true}
              onClose={handleTurnOffSettng}
              docInfo={[settingButtonInfo.docInfo]}
            />
          )}
          {modalType === "CHANGE_FOLDER_NAME" && (
            <CreateNewFolderModal
              open={true}
              workgroupId={settingButtonInfo.folderInfo?.workgroupId}
              targetFolderId={settingButtonInfo.folderInfo?.folderId}
              initFolderName={settingButtonInfo.folderInfo?.folderName}
              onClose={handleTurnOffSettng}
            />
          )}
          {modalType === "FOLDER_DELETE" && (
            <DeleteFolderModal
              open={true}
              targetFolderId={settingButtonInfo.folderInfo?.folderId}
              folderName={settingButtonInfo.folderInfo?.folderName}
              onClose={handleTurnOffSettng}
            />
          )}
          {modalType === "SHARE_FILE_TO_WORKGROUP" &&
            settingButtonInfo.docInfo && (
              <WorkgroupAddModal
                open={modalType === "SHARE_FILE_TO_WORKGROUP"}
                onClose={handleTurnOffSettng}
                uploadedFiles={[settingButtonInfo.docInfo]}
                createType={"SHARE_TO_WORKGROUP"}
              />
            )}
          {modalType === "RESTORE_FILE" && (
            <ConfirmDeleteOrRestoreModal
              open={modalType === "RESTORE_FILE"}
              onClose={handleTurnOffSettng}
              type="restore"
              syncIds={[settingButtonInfo.syncId]}
            />
          )}
          {modalType === "CHANGE_FILE_AUTH" && (
            <ChangeFileAuthModal
              open={modalType === "CHANGE_FILE_AUTH"}
              onClose={handleTurnOffSettng}
              docInfo={settingButtonInfo.docInfo}
            />
          )}
          {modalType === "CHANGE_FILE_AUTH_WORKGROUP" &&
            settingButtonInfo.docInfo && (
              <ChangeWorkgroupFileAuthModal
                open={modalType === "CHANGE_FILE_AUTH_WORKGROUP"}
                onClose={handleTurnOffSettng}
                docInfos={[settingButtonInfo.docInfo]}
              />
            )}
          {modalType === "DELETE_PERM" && (
            <ConfirmDeleteOrRestoreModal
              open={modalType === "DELETE_PERM"}
              onClose={handleTurnOffSettng}
              type={"delete"}
              syncIds={[settingButtonInfo.syncId]}
            />
          )}
        </>
      )}
    </>
  );
};
export default DocSettingButton;
